import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ToastActions from 'redux/toast';
import { CreateInvoiceRequest } from 'lib/types/requests';
import { getFirebaseContext } from 'utils/firebase';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import FullScreenModal from 'components/FullScreenModal';
import { getOrThrow } from 'lib/utils/refs';
import { selectUser } from 'redux/auth';
import LoadingState from 'components/LoadingState';
import { ESnapshotExists, ENotice, exists, ERate, EUser } from 'lib/types';
import { ResponseOrError, wrapError, wrapSuccess } from 'lib/types/responses';
import { safeGetOrThrow } from 'lib/safeWrappers';
import { logAndCaptureException } from 'utils';
import { ColumnService } from 'lib/services/directory';
import {
  INVOICE_CREATION_IN_PROGRESS_ERROR,
  NOTICE_HAS_INVOICE_ERROR
} from 'services/billing';
import { getDynamicRoute, useAppParams } from 'lib/frontend/utils/router';
import { NOTICE_ROUTES } from 'router/routes';
import { createNoticeInvoice } from 'routes/notice/billing/handleInvoices';
import { OrganizationModel } from 'lib/model/objects/organizationModel';
import { safeGetModelFromRef } from 'lib/model/getModel';
import InvoicePreviewCard from './InvoicePreviewCard';
import InvoiceDetailsCard from './InvoiceDetailsCard';
import {
  getInitialInvoiceCreationDataFromNotice,
  getInvoiceDataInvalidReason
} from './createInvoiceUtils';

const createInvoice = async (
  currentPublisherUser: ESnapshotExists<EUser>,
  notice: ESnapshotExists<ENotice>,
  invoiceCreationData: CreateInvoiceRequest
): Promise<ResponseOrError<undefined>> => {
  const ctx = getFirebaseContext();
  const { lineItems } = invoiceCreationData;
  const [publisherError, publisher] = await safeGetModelFromRef(
    OrganizationModel,
    ctx,
    notice.data().newspaper
  );

  if (publisherError) {
    return wrapError(publisherError);
  }

  const [advertiserSnapError, advertiserSnap] = await safeGetOrThrow(
    notice.data().filer
  );
  if (advertiserSnapError) {
    return wrapError(advertiserSnapError);
  }

  const [error] = await createNoticeInvoice({
    lineItems,
    noticeSnap: notice,
    newspaperSnap: publisher,
    advertiserSnap,
    user: currentPublisherUser,
    inAppTaxPct: invoiceCreationData.taxPct
  });

  if (error) {
    return wrapError(error);
  }

  return wrapSuccess(undefined);
};

export default function CreateInvoice() {
  const [invoiceCreationData, setInvoiceCreationData] =
    useState<CreateInvoiceRequest>();
  const [notice, setNotice] = useState<ESnapshotExists<ENotice>>();
  const [rate, setRate] = useState<ESnapshotExists<ERate>>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);

  const { id: noticeId } = useAppParams(NOTICE_ROUTES.INVOICE_CREATE);

  useEffect(() => {
    const setInitialData = async () => {
      const notice = await getFirebaseContext()
        .userNoticesRef()
        .doc(noticeId)
        .get();
      if (!exists(notice)) return;
      setNotice(notice);
      const rate = await getOrThrow(notice.data().rate);
      setRate(rate);
    };
    void setInitialData();
  }, []);

  useEffect(() => {
    void (async () => {
      if (!notice) return;
      const invoiceData = await getInitialInvoiceCreationDataFromNotice(notice);
      setInvoiceCreationData(invoiceData);
    })();
  }, [notice?.id]);

  const loadingComplete = !!invoiceCreationData && !!notice && !!rate && !!user;

  const invalidReason = getInvoiceDataInvalidReason(
    notice,
    rate,
    invoiceCreationData
  );

  return (
    <FullScreenModal
      headerText="Create Invoice"
      submittable={{
        buttonText: 'Create',
        onSubmit: async () => {
          if (!loadingComplete) return;
          const [error] = await createInvoice(
            user,
            notice,
            invoiceCreationData
          );

          if (error) {
            logAndCaptureException(
              ColumnService.PAYMENTS,
              error,
              '[New Create Invoice]: Failed to create a new invoice',
              { noticeId: notice.id }
            );
            dispatch(
              ToastActions.toastError({
                headerText: 'Failed to create an invoice',
                bodyText: [
                  INVOICE_CREATION_IN_PROGRESS_ERROR,
                  NOTICE_HAS_INVOICE_ERROR
                ].some(msg => error.message.includes(msg))
                  ? error.message
                  : 'Please try again or contact help@column.us'
              })
            );
          }

          navigate(getDynamicRoute(NOTICE_ROUTES.DETAIL, { id: notice.id }));
        },
        disabled: !!invalidReason
      }}
      onClose={() =>
        navigate(getDynamicRoute(NOTICE_ROUTES.DETAIL, { id: notice?.id }))
      }
      id="create-invoice"
    >
      <div className="grid grid-cols-5">
        {!loadingComplete && <LoadingState />}

        {loadingComplete && (
          <>
            <div className="col-span-3 overflow-scroll">
              <div className="max-w-4xl mx-auto">
                <InvoiceDetailsCard
                  updateInvoiceCreationData={invoiceDataUpdates => {
                    setInvoiceCreationData({
                      ...invoiceCreationData,
                      ...invoiceDataUpdates
                    });
                  }}
                  invoiceCreationData={invoiceCreationData}
                />
              </div>
            </div>
            <div className="col-span-2 border-l">
              <InvoicePreviewCard
                invoiceCreationData={invoiceCreationData}
                notice={notice}
              />
            </div>
          </>
        )}
      </div>
    </FullScreenModal>
  );
}
